/**
 * Hook for handling ordered chat streaming events.
 * 
 * Problem: SignalR/WebSocket events may arrive out of order due to network conditions.
 * Solution: Each event includes a sequence number (seq). This hook buffers and reorders
 * events to ensure tokens are processed in the correct order.
 * 
 * @module hooks/useOrderedChatStream
 */

import { useCallback, useRef } from 'react'
import type {
  ChatMessageStartPayload,
  ChatTokenPayload,
  ChatToolCallPayload,
  ChatToolResultPayload,
  ChatCompletePayload
} from '@/lib/signalRService'

/**
 * Buffered event waiting to be processed
 */
interface BufferedEvent {
  seq: number
  type: 'token' | 'toolCall' | 'toolResult' | 'complete'
  payload: ChatTokenPayload | ChatToolCallPayload | ChatToolResultPayload | ChatCompletePayload
}

/**
 * Options for the useOrderedChatStream hook
 */
export interface UseOrderedChatStreamOptions {
  /** Callback when a token is received (in order) */
  onToken: (token: string) => void
  /** Callback when a tool call is received (in order) */
  onToolCall: (payload: ChatToolCallPayload) => void
  /** Callback when a tool result is received (in order) */
  onToolResult: (payload: ChatToolResultPayload) => void
  /** Callback when the stream is complete */
  onComplete: (response: string) => void
  /** Maximum events to buffer before force-flushing (default: 100) */
  maxBufferSize?: number
  /** Timeout in ms to wait for missing events before force-processing (default: 2000) */
  reorderTimeoutMs?: number
}

/**
 * Return value from the useOrderedChatStream hook
 */
export interface UseOrderedChatStreamReturn {
  /** Handler for the turn-start event - pass to useSignalR's onChatMessageStart. Seeds the seq baseline. */
  handleMessageStart: (payload: ChatMessageStartPayload) => void
  /** Handler for incoming token events - pass to useSignalR's onChatToken */
  handleToken: (payload: ChatTokenPayload) => void
  /** Handler for incoming tool call events */
  handleToolCall: (payload: ChatToolCallPayload) => void
  /** Handler for incoming tool result events */
  handleToolResult: (payload: ChatToolResultPayload) => void
  /** Handler for stream completion */
  handleComplete: (payload: ChatCompletePayload) => void
  /** Reset the stream state (call when starting a new message) */
  reset: () => void
}

/**
 * Hook for handling ordered chat streaming with buffering and reordering.
 * 
 * @example
 * ```tsx
 * const { handleToken, handleComplete, reset } = useOrderedChatStream({
 *   onToken: (token) => setContent(prev => prev + token),
 *   onToolCall: (payload) => addToolCall(payload),
 *   onToolResult: (payload) => updateToolResult(payload),
 *   onComplete: (response) => finalizeMessage(response),
 * })
 * 
 * useSignalR({
 *   sessionId,
 *   onChatToken: handleToken,
 *   onChatToolCall: handleToolCall,
 *   onChatToolResult: handleToolResult,
 *   onChatComplete: handleComplete,
 * })
 * ```
 */
export function useOrderedChatStream(options: UseOrderedChatStreamOptions): UseOrderedChatStreamReturn {
  const {
    onToken,
    onToolCall,
    onToolResult,
    onComplete,
    maxBufferSize = 100,
    reorderTimeoutMs = 2000,
  } = options

  // Track the next expected sequence number
  const nextExpectedSeqRef = useRef(1)
  // Whether the seq baseline has been established for the current turn (D6). The producer assigns seq from the
  // first emitted event (MessageStart = 1), but MessageStart is not always routed to this hook — so we seed the
  // baseline from whatever event arrives first instead of hardcoding 1, otherwise the first token (seq 2) would
  // buffer against an expected-seq of 1 and only paint after the 2s force-flush.
  const seededRef = useRef(false)
  // Buffer for out-of-order events
  const bufferRef = useRef<BufferedEvent[]>([])
  // Timeout handle for force-flush
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  /**
   * Process a single event by type
   */
  const processEvent = useCallback((event: BufferedEvent) => {
    switch (event.type) {
      case 'token':
        onToken((event.payload as ChatTokenPayload).token)
        break
      case 'toolCall':
        onToolCall(event.payload as ChatToolCallPayload)
        break
      case 'toolResult':
        onToolResult(event.payload as ChatToolResultPayload)
        break
      case 'complete':
        onComplete((event.payload as ChatCompletePayload).response)
        break
    }
  }, [onToken, onToolCall, onToolResult, onComplete])

  /**
   * Try to flush buffered events in order
   */
  const flushBuffer = useCallback(() => {
    // Sort buffer by sequence number
    bufferRef.current.sort((a, b) => a.seq - b.seq)

    // Process events that are in order
    while (bufferRef.current.length > 0) {
      const next = bufferRef.current[0]
      if (next.seq === nextExpectedSeqRef.current) {
        bufferRef.current.shift()
        processEvent(next)
        nextExpectedSeqRef.current++
      } else if (next.seq < nextExpectedSeqRef.current) {
        // Duplicate or already processed - skip
        bufferRef.current.shift()
      } else {
        // Gap in sequence - stop and wait
        break
      }
    }
  }, [processEvent])

  /**
   * Force flush all buffered events (when timeout expires or buffer is full)
   */
  const forceFlush = useCallback(() => {
    if (bufferRef.current.length === 0) return

    console.warn(
      `[useOrderedChatStream] Force-flushing ${bufferRef.current.length} buffered events. ` +
      `Expected seq: ${nextExpectedSeqRef.current}, buffer seqs: ${bufferRef.current.map(e => e.seq).join(', ')}`
    )

    // Sort and process all remaining events
    bufferRef.current.sort((a, b) => a.seq - b.seq)
    for (const event of bufferRef.current) {
      processEvent(event)
    }
    
    // Update expected seq to be after the last processed event
    if (bufferRef.current.length > 0) {
      nextExpectedSeqRef.current = bufferRef.current[bufferRef.current.length - 1].seq + 1
    }
    bufferRef.current = []
  }, [processEvent])

  /**
   * Schedule a force-flush timeout
   */
  const scheduleForceFlush = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(forceFlush, reorderTimeoutMs)
  }, [forceFlush, reorderTimeoutMs])

  /**
   * Handle an incoming event
   */
  const handleEvent = useCallback((
    type: BufferedEvent['type'],
    payload: BufferedEvent['payload']
  ) => {
    const seq = payload.seq

    // Seed the baseline from the first event of the turn (D6) so we don't stall waiting for a seq-1 event that
    // is never delivered here (MessageStart, when not routed, consumes seq 1 upstream).
    if (!seededRef.current) {
      nextExpectedSeqRef.current = seq
      seededRef.current = true
    }

    // If this is the expected sequence, process immediately
    if (seq === nextExpectedSeqRef.current) {
      processEvent({ seq, type, payload })
      nextExpectedSeqRef.current++
      // Try to flush any buffered events that are now in order
      flushBuffer()
      return
    }

    // If this is an old/duplicate event, ignore it
    if (seq < nextExpectedSeqRef.current) {
      console.debug(`[useOrderedChatStream] Ignoring duplicate/old event with seq ${seq}`)
      return
    }

    // Out of order - buffer it
    bufferRef.current.push({ seq, type, payload })

    // Check if buffer is too large
    if (bufferRef.current.length >= maxBufferSize) {
      console.warn(`[useOrderedChatStream] Buffer full, force-flushing`)
      forceFlush()
      return
    }

    // Schedule force-flush timeout
    scheduleForceFlush()
  }, [processEvent, flushBuffer, forceFlush, maxBufferSize, scheduleForceFlush])

  const handleMessageStart = useCallback((payload: ChatMessageStartPayload) => {
    // MessageStart (confirmed direction (a)) renders nothing but pins the baseline: it consumes its own seq so
    // the following token processes in order. Seed-from-first (above) is the fallback when it isn't delivered.
    nextExpectedSeqRef.current = payload.seq + 1
    seededRef.current = true
  }, [])

  const handleToken = useCallback((payload: ChatTokenPayload) => {
    handleEvent('token', payload)
  }, [handleEvent])

  const handleToolCall = useCallback((payload: ChatToolCallPayload) => {
    handleEvent('toolCall', payload)
  }, [handleEvent])

  const handleToolResult = useCallback((payload: ChatToolResultPayload) => {
    handleEvent('toolResult', payload)
  }, [handleEvent])

  const handleComplete = useCallback((payload: ChatCompletePayload) => {
    // Complete event should flush everything first
    flushBuffer()
    // Then process the complete event
    onComplete(payload.response)
    // Reset for next message
    nextExpectedSeqRef.current = 1
    seededRef.current = false
    bufferRef.current = []
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [flushBuffer, onComplete])

  const reset = useCallback(() => {
    nextExpectedSeqRef.current = 1
    seededRef.current = false
    bufferRef.current = []
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  return {
    handleMessageStart,
    handleToken,
    handleToolCall,
    handleToolResult,
    handleComplete,
    reset,
  }
}

export default useOrderedChatStream
